/**
 * `/` — the portfolio dashboard (`Main.dc.html`).
 *
 * Real data, from one endpoint. Where a number does not exist yet it renders `—`, never
 * `0%`: a project with no closed audit has no score, and printing zero would read as a
 * failing project rather than an unaudited one.
 *
 * The KPI strip, attention queue and table are the artboard's. Audit-derived columns
 * populate from Phase 2 onwards; gate readiness and the attention queue still need
 * findings and the sign-off sheet, which are Phase 3.
 */

import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import type { MaturityLevel } from '@portal/domain';
import { Avatar, PermissionButton, ScoreRing, WarningGlyph, maturityLabel } from '@portal/ui';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const TH: CSSProperties = {
  ...LABEL,
  textAlign: 'left',
  padding: '7px 10px',
  background: 'var(--sunk)',
  borderBottom: '1px solid var(--bd)',
  whiteSpace: 'nowrap',
};

const TD: CSSProperties = {
  padding: '0 10px',
  height: 38,
  borderBottom: '1px solid var(--bd)',
  fontSize: 'var(--fs-row)',
  verticalAlign: 'middle',
};

const MATURITY_COLOUR: Record<MaturityLevel, string> = {
  L1: 'var(--ba)',
  L2: 'var(--wa)',
  L3: 'var(--ac)',
  L4: 'var(--ok)',
};

function Panel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        padding: '32px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  alarming = false,
}: {
  label: string;
  value: string;
  note: string;
  alarming?: boolean;
}) {
  return (
    <div
      style={{
        flex: 1,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        borderRight: '1px solid var(--bd)',
      }}
    >
      <span style={LABEL}>{label}</span>
      <span
        className="mono"
        style={{
          font: '600 30px/1 var(--font-sans)',
          letterSpacing: '-0.01em',
          color: alarming ? 'var(--ba)' : 'var(--tx)',
        }}
      >
        {value}
      </span>
      <span style={{ fontSize: 11, color: 'var(--mu)' }}>{note}</span>
    </div>
  );
}

export function PortfolioRoute() {
  const navigate = useNavigate();
  const portfolio = useQuery({ queryKey: ['portfolio'], queryFn: api.portfolio });
  const data = portfolio.data;

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
            Portfolio
          </h1>
          <div style={{ fontSize: 12.5, color: 'var(--mu)' }}>
            {data === undefined ? (
              'Loading…'
            ) : (
              <>
                {data.totals.projects} project{data.totals.projects === 1 ? '' : 's'} · checklist{' '}
                <span className="mono">v{data.checklistVersion}</span>
              </>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <PermissionButton disabledReason="Reporting arrives in Phase 5">Export</PermissionButton>
          <PermissionButton variant="primary" onClick={() => navigate('/projects/new')}>
            New project
          </PermissionButton>
        </div>
      </header>

      {portfolio.isPending && (
        <Panel>
          <span style={{ fontSize: 13, color: 'var(--mu)' }}>Loading the portfolio…</span>
        </Panel>
      )}

      {portfolio.isError && (
        <Panel>
          <span style={{ color: 'var(--ba)' }}>
            <WarningGlyph size={20} />
          </span>
          <h2 style={{ margin: 0, font: '600 15px/1.3 var(--font-sans)' }}>
            The portfolio could not be loaded
          </h2>
          {/* §7.4: the server's message is a sentence the user needs. Show it as-is. */}
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--mu)', maxWidth: 460, lineHeight: 1.55 }}>
            {portfolio.error instanceof ApiClientError
              ? portfolio.error.message
              : 'Something went wrong on our side.'}
          </p>
          <PermissionButton onClick={() => void portfolio.refetch()}>Try again</PermissionButton>
        </Panel>
      )}

      {data !== undefined && data.rows.length === 0 && (
        <Panel>
          <ScoreRing percent={null} size={88} caption="NO PROJECTS" />
          <h2 style={{ margin: 0, font: '600 15px/1.3 var(--font-sans)' }}>No projects registered yet</h2>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--mu)', maxWidth: 480, lineHeight: 1.6 }}>
            Registering a project links its repositories and sets the context profile, which decides
            how many of the 218 checklist items apply to it.
          </p>
          <PermissionButton variant="primary" onClick={() => navigate('/projects/new')}>
            Register the first project
          </PermissionButton>
        </Panel>
      )}

      {data !== undefined && data.rows.length > 0 && (
        <>
          <div
            style={{
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--r-lg)',
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            <Tile
              label="Projects"
              value={String(data.totals.projects)}
              note={`${data.rows.filter((row) => row.mandatoryPercent !== null).length} audited`}
            />
            <Tile
              label="Avg mandatory score"
              value={
                data.totals.averageMandatoryPercent === null
                  ? '—'
                  : `${Math.round(data.totals.averageMandatoryPercent)}%`
              }
              note={
                data.totals.averageMandatoryPercent === null
                  ? 'no closed audit yet'
                  : 'across audited projects'
              }
            />
            <Tile
              label="Gate-ready"
              value={`${data.totals.gateReady} / ${data.totals.projects}`}
              note="all 22 questions pass or waived"
            />
            <Tile
              label="Findings past deadline"
              value={String(data.totals.overdueFindings)}
              note="across all projects"
              alarming={data.totals.overdueFindings > 0}
            />
            <Tile
              label="Audits overdue"
              value={String(data.totals.overdueAudits)}
              note="past their due date"
              alarming={data.totals.overdueAudits > 0}
            />
          </div>

          <div
            style={{
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--r-lg)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                borderBottom: '1px solid var(--bd)',
              }}
            >
              <h2 style={{ margin: 0, font: '600 13px/1.3 var(--font-sans)' }}>Projects</h2>
              <span style={{ fontSize: 11, color: 'var(--mu)' }}>
                sorted by lowest mandatory score; unaudited projects last
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, paddingLeft: 14 }}>Project</th>
                    <th style={TH}>Client</th>
                    <th style={TH}>Repos</th>
                    <th style={{ ...TH, width: 150 }}>Mandatory score</th>
                    <th style={TH}>Maturity</th>
                    <th style={TH}>Gate</th>
                    <th style={TH}>Last audit</th>
                    <th style={TH}>Next audit</th>
                    <th style={TH}>Findings</th>
                    <th style={{ ...TH, paddingRight: 14 }}>Team</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.rows]
                    .sort((a, b) => {
                      if (a.mandatoryPercent === null && b.mandatoryPercent === null) {
                        return a.name.localeCompare(b.name);
                      }
                      if (a.mandatoryPercent === null) return 1;
                      if (b.mandatoryPercent === null) return -1;
                      return a.mandatoryPercent - b.mandatoryPercent;
                    })
                    .map((row) => (
                      <tr key={row.id}>
                        <td style={{ ...TD, paddingLeft: 14, fontWeight: 600 }}>
                          <Link to={`/projects/${row.id}`}>{row.name}</Link>
                        </td>
                        <td style={{ ...TD, color: 'var(--mu)' }}>{row.clientName}</td>
                        <td style={{ ...TD }} className="mono">
                          {row.repositoryCount}
                        </td>
                        <td style={TD}>
                          {row.mandatoryPercent === null ? (
                            <span style={{ color: 'var(--mu)' }} title="No closed audit yet — not the same as zero">
                              — not audited
                            </span>
                          ) : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  width: 96,
                                  height: 6,
                                  borderRadius: 3,
                                  background: 'var(--sunk)',
                                  overflow: 'hidden',
                                }}
                              >
                                <span
                                  style={{
                                    display: 'block',
                                    width: `${row.mandatoryPercent}%`,
                                    height: '100%',
                                    background:
                                      row.maturity === null
                                        ? 'var(--ac)'
                                        : MATURITY_COLOUR[row.maturity],
                                  }}
                                />
                              </span>
                              <span className="mono" style={{ fontWeight: 600, fontSize: 12 }}>
                                {Math.round(row.mandatoryPercent)}%
                              </span>
                            </span>
                          )}
                        </td>
                        <td style={TD}>
                          {row.maturity === null ? (
                            <span style={{ color: 'var(--mu)' }}>—</span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: MATURITY_COLOUR[row.maturity],
                              }}
                            >
                              <span
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: 2,
                                  background: MATURITY_COLOUR[row.maturity],
                                }}
                              />
                              {maturityLabel(row.maturity)}
                            </span>
                          )}
                        </td>
                        <td style={TD} className="mono">
                          {row.gatePassing === null || row.gateTotal === null ? (
                            <span style={{ color: 'var(--mu)' }} title="Gate readiness arrives in Phase 3">
                              —
                            </span>
                          ) : (
                            `${row.gatePassing}/${row.gateTotal}`
                          )}
                        </td>
                        <td style={{ ...TD, color: 'var(--mu)' }} className="mono">
                          {row.lastAudit === null
                            ? '—'
                            : `#${row.lastAudit.number} · ${new Date(row.lastAudit.closedAt).toLocaleDateString('en-GB')}`}
                        </td>
                        <td style={TD}>
                          {row.nextAudit === null ? (
                            <span style={{ color: 'var(--mu)' }}>none scheduled</span>
                          ) : row.nextAudit.overdueDays > 0 ? (
                            <span style={{ color: 'var(--ba)', fontWeight: 600 }}>
                              overdue {row.nextAudit.overdueDays} d
                            </span>
                          ) : (
                            <span className="mono">
                              {new Date(row.nextAudit.dueDate).toLocaleDateString('en-GB')}
                            </span>
                          )}
                        </td>
                        <td style={TD}>
                          {row.openFindings === 0 ? (
                            <span style={{ color: 'var(--mu)' }}>none</span>
                          ) : (
                            <span
                              style={{ color: row.overdueFindings > 0 ? 'var(--ba)' : 'var(--tx)' }}
                              className="mono"
                            >
                              {row.openFindings}
                              {row.overdueFindings > 0 && ` · ${row.overdueFindings} late`}
                            </span>
                          )}
                        </td>
                        <td style={{ ...TD, paddingRight: 14 }}>
                          <span style={{ display: 'flex' }}>
                            {row.auditorInitials.length === 0 ? (
                              <span style={{ color: 'var(--mu)', fontSize: 11.5 }}>unassigned</span>
                            ) : (
                              row.auditorInitials.map((initials, index) => (
                                <span key={initials} style={{ marginLeft: index === 0 ? 0 : -5 }}>
                                  <Avatar initials={initials} />
                                </span>
                              ))
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <p style={{ margin: 0, padding: '9px 14px', background: 'var(--sunk)', fontSize: 11, color: 'var(--mu)' }}>
              A score appears once a project has a <b>closed</b> audit — an unaudited project reads
              &ldquo;— not audited&rdquo;, never 0%. Gate readiness and the attention queue need
              findings and the sign-off sheet, which arrive in Phase 3.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
